/**
 * 案件・商品明細・案件ステータスの型と入力検証（02 5.3.6 case）。
 * 画面（React Hook Form の zodResolver）と API（ZodValidationPipe）で同じスキーマを使う（02 1.5・03 3.6.1）。
 * - 任意項目の空文字は null（02 2.10）。更新で null を送ると値を消す
 * - 本文に定義外のプロパティがあれば 400（D-019：strict）
 * - 金額は Decimal 文字列（zMoney）。応答も小数 2 桁の Decimal 文字列（02 2.11）
 * - 失注（is_lost のステータス）の失注理由の必須は、保存済みの値と合わせて service で確かめる（ステータスはマスタのため）
 */
import { z } from 'zod';
import { CUSTOMER_TYPE_VALUES } from '../enums/customer-type';
import { ITEM_PHASE_TYPE_VALUES } from '../enums/item-phase-type';
import { zCode, zDate, zMoney, zPercent, zRequiredText, zShortText, zSystemCode, zVersion } from './common.schema';
import { zNullable } from './customer.schema';
import { buildListQuerySchema, CommonAttributesSchema, DeletedFilterSchema } from './list-query.schema';

/** 必須の選択（顧客など）。未入力の文言を項目別にする（例 MSG-V-059「顧客を選択してください」） */
export function zRequiredCode(required_message_id: string) {
  return z.string({ message: required_message_id }).trim().min(1, required_message_id).max(50, 'MSG-V-020');
}

/** 案件・取引・活動の登録で共通の参照先（顧客・企業担当者・担当営業） */
export const SALES_REFERENCE_SHAPE = {
  customer_code: zRequiredCode('MSG-V-059'),
  customer_contact_code: zNullable(zSystemCode).optional(),
  /** 担当営業は必ず値を持つ（D-037）。省略は「登録した利用者」、null は受け付けない */
  sales_user_code: zSystemCode.optional(),
};

const CASE_INPUT_SHAPE = {
  case_name: zRequiredText(200, 'MSG-V-044'),
  case_summary: zShortText(2000).optional(),
  ...SALES_REFERENCE_SHAPE,
  case_status: zCode.optional(),
  amount: zNullable(zMoney).optional(),
  probability: zNullable(zPercent).optional(),
  due_date: zNullable(zDate).optional(),
  first_contact_date: zNullable(zDate).optional(),
  lost_reason: zShortText(1000).optional(),
};

/** API-CASE-02 案件の登録 */
export const CaseCreateSchema = z.object(CASE_INPUT_SHAPE).strict();

/** API-CASE-04 案件の更新（すべて任意＋version） */
export const CaseUpdateSchema = z
  .object({
    ...CASE_INPUT_SHAPE,
    case_name: CASE_INPUT_SHAPE.case_name.optional(),
    customer_code: CASE_INPUT_SHAPE.customer_code.optional(),
    version: zVersion,
  })
  .strict();

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const ActivityCountsSchema = z.object({
  visit: z.number().int(),
  online: z.number().int(),
  phone: z.number().int(),
  email: z.number().int(),
});

/** API-CASE-01 の 1 行（CaseSummary：activity_counts・elapsed_days を除く） */
export const CaseSummarySchema = z
  .object({
    case_code: z.string(),
    case_name: z.string(),
    case_summary: z.string().nullable(),
    customer_code: z.string(),
    customer_name: z.string().nullable(),
    customer_type: z.enum(CUSTOMER_TYPE_VALUES).nullable(),
    customer_contact_code: z.string().nullable(),
    customer_contact_name: z.string().nullable(),
    sales_user_code: z.string(),
    sales_user_name: z.string().nullable(),
    case_status: z.string(),
    case_status_name: z.string().nullable(),
    is_won: z.boolean(),
    is_lost: z.boolean(),
    amount: z.string().nullable(),
    probability: z.number().int().nullable(),
    weighted_amount: z.string().nullable(),
    due_date: z.string().nullable(),
    first_contact_date: z.string().nullable(),
    lost_reason: z.string().nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

/** API-CASE-03 の案件 1 件（種別別の活動件数・経過日数を含む） */
export const CaseSchema = CaseSummarySchema.extend({
  activity_counts: ActivityCountsSchema,
  elapsed_days: z.number().int().nullable(),
});

/** API-CASE-04 の応答（受注のステータスに変わったら suggest_transaction_creation=true：D-039） */
export const CaseUpdateResponseSchema = CaseSchema.extend({ suggest_transaction_creation: z.boolean() });

export const CaseStatusSchema = z.object({
  case_status: z.string(),
  case_status_name: z.string(),
  display_order: z.number().int(),
  is_won: z.boolean(),
  is_lost: z.boolean(),
});

export const CASE_LIST_SORT_VALUES = ['case_code', 'case_name', 'created_at', 'due_date', 'amount'] as const;

/** API-CASE-01（02 2.3.2） */
export const CaseListQuerySchema = buildListQuerySchema(CASE_LIST_SORT_VALUES, '-created_at').extend({
  case_status: zCode.optional(),
  customer_code: zCode.optional(),
  sales_user_code: zSystemCode.optional(),
  due_date_from: zDate.optional(),
  due_date_to: zDate.optional(),
});

// ───────── 商品明細 ─────────

const CASE_ITEM_INPUT_SHAPE = {
  product_name: zRequiredText(200, 'MSG-V-069'),
  product_type: zShortText(100).optional(),
  item_phase_type: z.enum(ITEM_PHASE_TYPE_VALUES, { message: 'MSG-V-021' }),
  amount: zNullable(zMoney).optional(),
  supplier_name: zShortText(200).optional(),
  note: zShortText(2000).optional(),
};

/** API-CASE-09 商品明細の登録 */
export const CaseItemCreateSchema = z.object(CASE_ITEM_INPUT_SHAPE).strict();

/** API-CASE-10 商品明細の更新 */
export const CaseItemUpdateSchema = z
  .object({
    ...CASE_ITEM_INPUT_SHAPE,
    product_name: CASE_ITEM_INPUT_SHAPE.product_name.optional(),
    item_phase_type: CASE_ITEM_INPUT_SHAPE.item_phase_type.optional(),
    version: zVersion,
  })
  .strict();

export const CaseItemSchema = z
  .object({
    case_code: z.string(),
    item_number: z.number().int(),
    product_name: z.string(),
    product_type: z.string().nullable(),
    item_phase_type: z.enum(ITEM_PHASE_TYPE_VALUES),
    amount: z.string().nullable(),
    supplier_name: z.string().nullable(),
    note: z.string().nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

/** API-CASE-08 のクエリ（明細は案件あたり数十件のためページングしない） */
export const CaseItemListQuerySchema = z.object({ deleted: DeletedFilterSchema.default('exclude') });

/** API-CASE-08 の meta（合計金額：仕様書 F-006） */
export const CaseItemListMetaSchema = z.object({ total_amount: z.string(), total_count: z.number().int() });

/** パス `{case_code}/items/{item_number}` */
export const CaseItemPathSchema = z.object({
  case_code: zCode,
  item_number: z.coerce.number({ message: 'MSG-V-020' }).int('MSG-V-020').min(1, 'MSG-V-020'),
});

export type CaseCreateInput = z.infer<typeof CaseCreateSchema>;
export type CaseUpdateInput = z.infer<typeof CaseUpdateSchema>;
export type CaseListQuery = z.infer<typeof CaseListQuerySchema>;
export type CaseSummary = z.infer<typeof CaseSummarySchema>;
export type Case = z.infer<typeof CaseSchema>;
export type CaseUpdateResponse = z.infer<typeof CaseUpdateResponseSchema>;
export type CaseStatusMaster = z.infer<typeof CaseStatusSchema>;
export type ActivityCounts = z.infer<typeof ActivityCountsSchema>;
export type CaseItemCreateInput = z.infer<typeof CaseItemCreateSchema>;
export type CaseItemUpdateInput = z.infer<typeof CaseItemUpdateSchema>;
export type CaseItem = z.infer<typeof CaseItemSchema>;
export type CaseItemListMeta = z.infer<typeof CaseItemListMetaSchema>;
